import { writable, derived } from 'svelte/store';
import { referenceService } from '../services/referenceService';
import { figureService } from '../services/figureService';

// Staleness threshold in milliseconds (30 seconds)
const STALE_THRESHOLD = 30000;

/**
 * Creates a centralized store for managing figure data from Patdraw.
 * This serves as the single source of truth for all figure-related state,
 * ensuring consistent data transformation and reactive updates across components.
 */
function createFiguresStore() {
  const { subscribe, set: _set, update } = writable({
    figures: [],
    loading: false,
    error: null,
    lastFetch: null
  });

  // Track if a refresh is already in progress to prevent duplicates
  let refreshPromise = null;

  return {
    subscribe,
    
    /**
     * Check if the data is stale based on the last fetch time
     * @returns {boolean} True if data is older than STALE_THRESHOLD
     */
    isStale() {
      let currentState;
      const unsubscribe = this.subscribe(state => {
        currentState = state;
      });
      unsubscribe();
      
      if (!currentState.lastFetch) return true;
      return Date.now() - currentState.lastFetch > STALE_THRESHOLD;
    },
    
    /**
     * Load figures from Patdraw API with smart refresh and deduplication
     * @param {boolean} forceRefresh - If true, clears cache and fetches fresh data
     * @param {boolean} silent - If true, doesn't show loading state (for background refresh)
     * @returns {Promise} Resolves when figures are loaded
     */
    async loadFigures(forceRefresh = false, silent = false) {
      // If not forcing refresh and data is fresh, skip loading
      if (!forceRefresh && !this.isStale()) {
        return Promise.resolve();
      }
      
      // If a refresh is already in progress, return the existing promise
      if (refreshPromise) {
        return refreshPromise;
      }
      
      // Create new refresh promise
      refreshPromise = (async () => {
        // Only show loading state if not a silent refresh
        if (!silent) {
          update(state => ({ ...state, loading: true, error: null }));
        }
        
        try {
          if (forceRefresh) {
            figureService.clearCache();
          }
          
          // Single transformation point - use unified reference service
          // This ensures consistent data structure with proper URL formatting
          const figures = await referenceService.getMenuItems();
          
          // Update figures and clear loading state
          update(state => ({
            ...state,
            figures,
            loading: false,
            error: null,
            lastFetch: Date.now()
          }));
          
        } catch (error) {
          console.error('[FiguresStore] Failed to load figures:', error);
          update(state => ({ 
            ...state, 
            loading: false, 
            error: error.message || 'Failed to load figures'
          }));
        } finally {
          refreshPromise = null;
        }
      })();
      
      return refreshPromise;
    },
    
    /**
     * Refresh figures by clearing cache and reloading
     * @param {boolean} silent - If true, refreshes in background without loading UI
     */
    async refresh(silent = false) {
      return this.loadFigures(true, silent);
    },
    
    /**
     * Silent refresh - updates figures in background without UI interruption
     */
    async silentRefresh() {
      return this.loadFigures(true, true);
    },
    
    /**
     * Clear any existing error state
     */
    clearError() {
      update(state => ({ ...state, error: null }));
    },
    
    /**
     * Filter figures based on search query
     * @param {string} query - Search query to filter figures
     * @returns {Array} Filtered array of figures
     */
    filterFigures(query) {
      let figures = [];
      const unsubscribe = this.subscribe(state => {
        figures = state.figures;
      });
      unsubscribe();
      
      if (!query) return figures;
      
      const lowerQuery = query.toLowerCase();
      return figures.filter(fig => {
        const searchString = `${fig.name} ${fig.type}`.toLowerCase();
        return searchString.includes(lowerQuery);
      });
    }
  };
}

// Create and export the singleton store instance
export const figuresStore = createFiguresStore();

// Derived store that provides a reactive filter function
export const createFilteredFigures = (queryStore) => {
  return derived(
    [figuresStore, queryStore],
    ([$figuresStore, $query]) => {
      if (!$query) return $figuresStore.figures;
      
      const lowerQuery = $query.toLowerCase();
      return $figuresStore.figures.filter(fig => {
        const searchString = `${fig.name} ${fig.type}`.toLowerCase();
        return searchString.includes(lowerQuery);
      });
    }
  );
};